/**
 * ESCHER FALLS — ゲーム状態機械（CORE-SPEC §3.1 / MODULE-CONTRACTS §10）。
 *
 * three.js に依存しない。カメラと水は `RotationDriver` / `FlowDriver` として DI で受け取る。
 * ヘッドレスの vitest で「回転補間の途中フレームで接続が成立しない」ことを検証できるようにするため。
 *
 * このモジュールの最重要規則:
 * **`buildGraph()` を呼ぶのは `loadLevel()` と `rotating → idle` の遷移の瞬間だけである。**
 * 補間途中の view でグラフを組むと数値的に偽の接続が成立する（PROJECTION-VERIFIED §6）。
 */

import type {
  ConnectionGraph,
  FlowPlan,
  GamePhase,
  GameState,
  Level,
  Piece,
  ViewPose,
  ViewRelation,
} from '../core/types';
import { buildGraph, isReachable, planFlow } from '../core/graph';
import { expandPieces, findDrainPorts, findGoalPorts, findSourcePort, hasLever } from '../core/pieces';
import { relationOf } from '../core/projection';

// =============================================================================
// 公開インターフェース（MODULE-CONTRACTS §10）
// =============================================================================

/** `render/cameraRig.ts` の `CameraRig` が構造的にこれを満たす。 */
export interface RotationDriver {
  /** 確定している姿勢。遷移中は遷移前の値。 */
  readonly pose: ViewPose;
  /** 方位回転・仰角移動のいずれかを補間中か。 */
  readonly isRotating: boolean;
  /** 方位を回す。受け付けたら true。遷移中は false。 */
  rotate(dir: -1 | 1): boolean;
  /** 仰角を変える。受け付けたら true。遷移中と端では false。 */
  elevate(dir: -1 | 1): boolean;
  /** @returns この呼び出しで遷移が完了したら true。**途中では決して true を返さない。** */
  update(dt: number): boolean;
  snapTo(pose: ViewPose): void;
}

/** `render/water.ts` の `WaterAnimator` が構造的にこれを満たす。 */
export interface FlowDriver {
  play(plan: FlowPlan): void;
  update(dt: number): { readonly finished: boolean; readonly goalReached: boolean };
  reset(): void;
}

export interface GameDeps {
  readonly rotation: RotationDriver;
  readonly flow: FlowDriver;
}

/** 接続グラフが組み直されたときの通知。 */
export type GraphRebuildListener = (graph: ConnectionGraph, relation: ViewRelation) => void;

export interface GameController {
  /** 現在のスナップショット。毎回同じ参照とは限らない（更新のたびに作り直す）。 */
  readonly state: GameState;
  /** レベルをロードして idle にする。graph を startView で構築する。 */
  loadLevel(level: Level): void;
  /** 方位回転の入力。受け付けたら true。 */
  rotate(dir: -1 | 1): boolean;
  /** 仰角移動の入力。受け付けたら true。端では false。 */
  elevate(dir: -1 | 1): boolean;
  /** レバーを倒す。受け付けたら true。レバーの無い面では false。 */
  toggleLever(): boolean;
  /** 放水。受け付けたら true。 */
  pour(): boolean;
  /** リトライ。現在のレベルを startView で読み直す。 */
  retry(): void;
  /** 水だけ引いて idle に戻す。視点は現在のまま。失敗からの復帰に使う。 */
  reset(): void;
  /**
   * 制限時間を使い切ったので `failed` に落とす。落としたら true。
   * 時間の管理は状態機械の外（main.ts のタイマー）にある。
   */
  timeUp(): boolean;
  /**
   * 時間切れから復帰する。**制限時間を延ばした側が呼ぶ。**
   * 延ばす判断はここではしない。
   */
  resumeFromTimeUp(): boolean;
  /** メインループから毎フレーム呼ぶ。dt はミリ秒。 */
  update(dt: number): void;
  /** 状態変化のたびに呼ばれる。戻り値は購読解除関数。 */
  subscribe(listener: (state: GameState) => void): () => void;
  /** `subscribe` の別名（呼び名の揺れを吸収するためのエイリアス）。 */
  onChange(listener: (state: GameState) => void): () => void;
  /**
   * 「接続グラフを組み直した」通知。`loadLevel()` と `rotating → idle` の瞬間だけ発火する。
   * デバッグ表示やアニメーション側が、再構築のタイミングを外から観測するために使う。
   */
  onGraphRebuild(listener: GraphRebuildListener): () => void;
}

// =============================================================================
// 純粋なヘルパ（副作用なし。ここに切り出しておくとテストしやすい）
// =============================================================================

/** レベル未ロードの空グラフ。`buildGraph` を呼ばずに作れるようリテラルで持つ。 */
const EMPTY_GRAPH: ConnectionGraph = {
  relation: 0,
  ports: new Map(),
  segments: new Map(),
  links: [],
  adjacency: new Map(),
};

/** `loadLevel` 前のプレースホルダ。`GameState` を non-nullable に保つためだけに存在する。 */
const EMPTY_LEVEL: Level = {
  id: '',
  name: '',
  startView: { azimuth: 0, elevation: 0 },
  waterBudget: null,
  timeLimitMs: null,
  maxMoves: null,
  hint: null,
  pieces: [],
};

const EMPTY_STATE: GameState = {
  level: EMPTY_LEVEL,
  pieces: [],
  phase: 'idle',
  pose: { azimuth: 0, elevation: 0 },
  relation: 0,
  graph: EMPTY_GRAPH,
  canPour: false,
  goalReachable: false,
  leverOn: false,
  hasLever: false,
  flow: null,
  flowTime: 0,
  rotations: 0,
  timedOut: false,
};

/**
 * 放水ボタンを押せるか（CORE-SPEC §3.1）。
 *
 * **接続の成否を見てはならない。** 水源とゴールさえ在れば常に押せる。
 * かつてここで到達可能性を判定していたが、それだと「押せた ＝ 正解」となり、
 * 盤面を一切見ずに最大3回の総当たりで全レベルが解けてしまっていた。
 * つながっていない状態で放水したときは、水が切れ目からこぼれて `failed` になる。
 */
export function computeCanPour(pieces: readonly Piece[]): boolean {
  return findSourcePort(pieces) !== null && findGoalPorts(pieces).length > 0;
}

/**
 * 現在の view で水源→ゴールが到達可能か。
 * **UI から参照してはならない**（`?debug=1` と検証ツール専用）。
 */
export function computeGoalReachable(
  pieces: readonly Piece[],
  graph: ConnectionGraph,
): boolean {
  const src = findSourcePort(pieces);
  const goals = findGoalPorts(pieces);
  if (src === null || goals.length === 0) return false;
  // **全てのゴールに届いて初めて解けたとみなす。**
  return goals.every((g) => isReachable(graph, src.id, g.id));
}

/**
 * この1手を使うと上限を超えるか（METAGAME-SPEC §5）。
 *
 * **回し始める前**に判定する。回転を開始してから `failed` にすると、
 * `update` が `rotation.update` を呼ばなくなってカメラが補間の途中で凍る。
 * 二周目専用の修飾で、`maxMoves` が `null` の面（本編）では常に false。
 */
function wouldExceedMoves(state: GameState): boolean {
  const limit = state.level.maxMoves;
  return limit !== null && state.rotations + 1 > limit;
}

/** 全フィールドの浅い一致。1つでも違えば購読者へ通知する。 */
function sameState(a: GameState, b: GameState): boolean {
  return (
    a.level === b.level &&
    a.pieces === b.pieces &&
    a.phase === b.phase &&
    a.pose === b.pose &&
    a.relation === b.relation &&
    a.leverOn === b.leverOn &&
    a.graph === b.graph &&
    a.canPour === b.canPour &&
    a.goalReachable === b.goalReachable &&
    a.flow === b.flow &&
    a.flowTime === b.flowTime &&
    a.rotations === b.rotations &&
    a.timedOut === b.timedOut
  );
}

/** レベルをロードした直後の状態を組む純関数。ドライバは触らない。 */
function makeLoadedState(level: Level, pieces: readonly Piece[]): GameState {
  const relation = relationOf(level.startView);
  const graph = buildGraph(pieces, relation);
  return {
    level,
    pieces,
    phase: 'idle',
    pose: level.startView,
    relation,
    graph,
    leverOn: false,
    hasLever: hasLever(level.pieces),
    canPour: computeCanPour(pieces),
    goalReachable: computeGoalReachable(pieces, graph),
    flow: null,
    flowTime: 0,
    rotations: 0,
    timedOut: false,
  };
}

// =============================================================================
// 本体
// =============================================================================

export function createGame(deps: GameDeps): GameController {
  let current: GameState = EMPTY_STATE;
  const listeners = new Set<(state: GameState) => void>();
  const graphListeners = new Set<GraphRebuildListener>();

  /** 状態を差し替え、変化があれば通知する。副作用はこの関数に閉じている。 */
  function commit(next: GameState): void {
    if (sameState(current, next)) return;
    current = next;
    // 通知中に購読解除されても走査が壊れないようコピーしてから回す。
    for (const listener of [...listeners]) listener(current);
  }

  function announceGraph(graph: ConnectionGraph, relation: ViewRelation): void {
    for (const listener of [...graphListeners]) listener(graph, relation);
  }

  function loadLevel(level: Level): void {
    const pieces = expandPieces(level.pieces);
    // ドライバを先に初期化してから状態を確定させる（購読者が整合した世界を見るため）。
    deps.rotation.snapTo(level.startView);
    deps.flow.reset();
    const next = makeLoadedState(level, pieces);
    commit(next);
    announceGraph(next.graph, next.relation);
  }

  function rotate(dir: -1 | 1): boolean {
    // 失敗表示から回そうとしたら、黙って水を引いてから回す（CORE-SPEC §3.4）。
    // 「もういちど」を押させてから回させるのは一手多く、試行の流れが切れる。
    // ここに置くのは、UI 側ではなく状態機械の規則だからである（呼び出し元を問わず成り立つ）。
    // 時間切れからは戻さない。水を引いても時間は戻らないので、ここで idle に
    // 戻すと次のフレームでまた時間切れに落ちるだけ（点滅して壊れて見える）。
    if (current.timedOut) return false;
    if (current.phase === 'failed') reset();
    // rotating / flowing / clear では入力を一切受け付けない。キューイングもしない。
    if (current.phase !== 'idle') return false;
    // 手数を使い切っていたら、回さずにその場で失敗させる。
    if (wouldExceedMoves(current)) {
      commit({ ...current, phase: 'failed' });
      return false;
    }
    if (!deps.rotation.rotate(dir)) return false;
    commit({ ...current, phase: 'rotating', rotations: current.rotations + 1 });
    return true;
  }

  /**
   * 仰角を変える。方位回転と同じ規則で扱う（同じ `rotating` フェーズに入る）。
   *
   * **方位と仰角を同時に動かしてはならない**（PROJECTION-9VIEW §3.8.6：
   * 方向が球面上の2次元パッチを掃き、ゴースト接続が爆発的に増える）。
   * `phase !== 'idle'` で弾いているので、同時遷移は構造的に起こらない。
   */
  function elevate(dir: -1 | 1): boolean {
    // 時間切れからは戻さない。水を引いても時間は戻らないので、ここで idle に
    // 戻すと次のフレームでまた時間切れに落ちるだけ（点滅して壊れて見える）。
    if (current.timedOut) return false;
    if (current.phase === 'failed') reset();
    if (current.phase !== 'idle') return false;
    if (wouldExceedMoves(current)) {
      commit({ ...current, phase: 'failed' });
      return false;
    }
    if (!deps.rotation.elevate(dir)) return false; // 端なら false が返る
    commit({ ...current, phase: 'rotating', rotations: current.rotations + 1 });
    return true;
  }

  /**
   * レバーを倒し、`shift` を持つピースを動かして接続グラフを組み直す。
   *
   * **切り替えは瞬間的**なので、補間の途中という状態が存在しない。
   * したがって「再構築は停止した瞬間のみ」という規則を自動的に満たす
   * （カメラの遷移と違い、ここにゴースト接続の危険は無い）。
   *
   * 手数を1消費する。視点を回すのと同じ重みの一手として扱う。
   */
  function toggleLever(): boolean {
    // 時間切れからは戻さない。水を引いても時間は戻らないので、ここで idle に
    // 戻すと次のフレームでまた時間切れに落ちるだけ（点滅して壊れて見える）。
    if (current.timedOut) return false;
    if (current.phase === 'failed') reset();
    if (current.phase !== 'idle') return false;
    if (!current.hasLever) return false;
    if (wouldExceedMoves(current)) {
      commit({ ...current, phase: 'failed' });
      return false;
    }
    const leverOn = !current.leverOn;
    const pieces = expandPieces(current.level.pieces, leverOn);
    const graph = buildGraph(pieces, current.relation);
    const next: GameState = {
      ...current,
      pieces,
      leverOn,
      graph,
      canPour: computeCanPour(pieces),
      goalReachable: computeGoalReachable(pieces, graph),
      rotations: current.rotations + 1,
      flow: null,
      flowTime: 0,
    };
    commit(next);
    announceGraph(graph, next.relation);
    return true;
  }

  function pour(): boolean {
    // つながっていなくても放水できる。届かなければ切れ目でこぼれて failed になる。
    if (current.phase !== 'idle' || !current.canPour) return false;
    const src = findSourcePort(current.pieces);
    const goals = findGoalPorts(current.pieces);
    // canPour が true なら両方存在するが、型を絞るために確認する。
    if (src === null || goals.length === 0) return false;

    const plan = planFlow(
      current.graph,
      src.id,
      goals.map((g) => g.id),
      current.level.waterBudget,
      findDrainPorts(current.pieces).map((d) => d.id),
    );
    deps.flow.play(plan);
    commit({ ...current, phase: 'flowing', flow: plan, flowTime: 0 });
    return true;
  }

  function retry(): void {
    loadLevel(current.level);
  }

  /**
   * 水だけを引いて `idle` に戻す。**視点は現在のまま保つ。**
   *
   * 失敗のたびに `startView` まで戻されると、切れ目を見つけて直す作業が
   * 毎回やり直しになって苛立たしい。失敗は「どこで切れているか」を見せるための
   * ものなので、見えている盤面のまま回し直せる必要がある。
   */
  function reset(): void {
    if (current.phase === 'rotating') return; // 補間中に割り込ませない
    deps.flow.reset();
    const graph = buildGraph(current.pieces, current.relation);
    const next: GameState = {
      ...current,
      phase: 'idle',
      graph,
      canPour: computeCanPour(current.pieces),
      goalReachable: computeGoalReachable(current.pieces, graph),
      flow: null,
      flowTime: 0,
      // timedOut はあえて引き継ぐ。水を引いても時間は戻らないので、
      // ここで消すと「もういちど」で時間切れを無かったことにできてしまう。
    };
    commit(next);
    announceGraph(graph, next.relation);
  }

  /**
   * 制限時間を使い切った。`idle` / `rotating` / `flowing` のどこからでも落ちる。
   *
   * 時間の管理は状態機械の外（main.ts のタイマー）にある。ここで経過時間を
   * 持たないのは、タイマーが「タブが隠れたら止まる」「ステージ選択中は止まる」
   * といった**画面側の事情**を知っている必要があるから。
   * 状態機械はその判断結果だけを受け取る。
   *
   * @returns 実際に時間切れへ落としたか（すでに終わっていれば false）
   */
  function timeUp(): boolean {
    if (current.phase === 'clear' || current.phase === 'failed') return false;
    deps.flow.reset();
    commit({ ...current, phase: 'failed', timedOut: true, flow: null, flowTime: 0 });
    return true;
  }

  /**
   * 時間切れから復帰する。制限時間を延ばした側（main.ts）が呼ぶ。
   * **延ばす判断はここではしない。** ここは「時間切れではなくなった」ことだけを扱う。
   */
  function resumeFromTimeUp(): boolean {
    if (!current.timedOut) return false;
    const graph = buildGraph(current.pieces, current.relation);
    commit({
      ...current,
      phase: 'idle',
      graph,
      canPour: computeCanPour(current.pieces),
      goalReachable: computeGoalReachable(current.pieces, graph),
      flow: null,
      flowTime: 0,
      timedOut: false,
    });
    announceGraph(graph, current.relation);
    return true;
  }

  function update(dt: number): void {
    let next = current;
    let rebuilt: ConnectionGraph | null = null;

    switch (current.phase) {
      case 'rotating': {
        const done = deps.rotation.update(dt);
        if (done) {
          // ★ ここが唯一の再構築点。done が false のフレームでは graph に触れない。
          // 仰角遷移では |d| = 2.12 の完全一致ゴーストが 5〜28ms 滞在するので、
          // ここを緩めるとゲームが成立しない（PROJECTION-9VIEW §3.8）。
          const pose = deps.rotation.pose;
          const relation = relationOf(pose);
          const graph = buildGraph(current.pieces, relation);
          next = {
            ...current,
            phase: 'idle',
            pose,
            relation,
            graph,
            canPour: computeCanPour(current.pieces),
            goalReachable: computeGoalReachable(current.pieces, graph),
          };
          rebuilt = graph;
        }
        break;
      }
      case 'flowing': {
        const progress = deps.flow.update(dt);
        // **涸れ井戸に入ったら、ゴールに届いていても失敗。**
        // 「繋げてはいけない」という負の条件は、ここで勝ちを取り消す形で効く。
        const drained = current.flow?.drained === true;
        const phase: GamePhase = drained
          ? progress.finished
            ? 'failed'
            : 'flowing'
          : progress.goalReached
            ? 'clear'
            : progress.finished
              ? 'failed'
              : 'flowing';
        next = { ...current, phase, flowTime: current.flowTime + dt };
        break;
      }
      case 'clear': {
        // 演出を進めるだけ。ここから遷移はしない（水は残したまま）。
        deps.flow.update(dt);
        next = { ...current, flowTime: current.flowTime + dt };
        break;
      }
      case 'idle':
      case 'failed':
        break;
    }

    commit(next);
    if (rebuilt !== null) announceGraph(rebuilt, next.relation);
  }

  function subscribe(listener: (state: GameState) => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function onGraphRebuild(listener: GraphRebuildListener): () => void {
    graphListeners.add(listener);
    return () => {
      graphListeners.delete(listener);
    };
  }

  return {
    get state(): GameState {
      return current;
    },
    loadLevel,
    rotate,
    elevate,
    toggleLever,
    pour,
    retry,
    reset,
    timeUp,
    resumeFromTimeUp,
    update,
    subscribe,
    onChange: subscribe,
    onGraphRebuild,
  };
}
